// Hook to handle errors to show error messages to user via toast and log to Sentry.
// For the hook to work, needs backend to use toApiErrorResponse from errorStandardize.ts
import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useToast } from "@/components/application/toast";

/**
 * Hook to handle errors in a consistent way across the app.
 * User context is automatically included via AuthProvider's Sentry.setUser()
 */
export function useHandleError() {
    const { toast } = useToast();

    const showToast = useCallback(
        (message: string, variant: "error" | "warning" = "error") => {
            toast({
                title: variant === "error" ? "Something went wrong" : "Warning",
                description: message,
                variant,
                // Keep your provider default unless you want errors to persist:
                // durationMs: variant === "error" ? 0 : undefined,
            });
        },
        [toast],
    );

    return useCallback(
        (error: unknown, context?: string) => {
            let message = "An unexpected error occurred.";

            if (error instanceof Error) {
                message = error.message;
            } else if (typeof error === "string") {
                message = error;
            } else if (error && typeof error === "object" && "message" in error) {
                message = String((error as { message?: unknown }).message);
            }

            const fullMessage = context ? `${context}: ${message}` : message;

            // Show error to user (toast)
            showToast(fullMessage, "error");

            // Log to console in development (Vite)
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.error("Error:", {
                    context,
                    error,
                    message: fullMessage,
                });
            }

            // Log to Sentry (user context is automatically included)
            Sentry.captureException(error, {
                tags: {
                    context: context || "unknown",
                },
                extra: {
                    message: fullMessage,
                },
            });
        },
        [showToast],
    );
}
